"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { clearOpenSabineFlag, hasOpenSabineFlag } from "@/lib/sabine-chat/open-link";

/**
 * Whether the page's registered coach (SideNavSlots `coach`) is open, and
 * whether it fills the viewport. The page may own `open` itself (a result
 * page opens the chat from a one-click coaching action), in which case the
 * nav relays through its `onOpenChange`; otherwise the nav keeps it, closed
 * unless the page asked for `defaultOpen` (the home page). Below `md` there
 * is no rail to open beside, so open means fullscreen, and a default-open
 * chat stays closed rather than covering the page. Both reset when the route
 * changes, like the rest of the nav's transient state.
 *
 * A chat that opens with its page is open from the first paint, and nothing
 * moves: `pageOpensChat` is what the frame knows from the route alone, so
 * the server draws the column open before the page's chat has registered
 * (the chat fills it on hydration), and the open settles in a layout
 * effect, before paint, on a client navigation too. While the open is the
 * page's rather than the person's, `instant` is true and the shells draw
 * without their transitions; the first Ask, close or fullscreen is theirs
 * and moves as before.
 *
 * A link from the chat library (`openSabineHref`) opens it on arrival, once
 * the arriving page's chat has read the flag (NavCoach `openOnArrival`), and
 * the flag leaves the address so a reload does not open it again.
 *
 * Closing hands focus back to whatever opened the chat, when the chat had
 * it and the opener is still on the page (the mobile header's button; the
 * strip's Ask button is replaced while the chat is open, so there focus is
 * left to the browser).
 *
 * @param {object} args
 * @param {object | null} args.coach
 * @param {boolean | undefined} args.hasRail  `undefined` until measured
 * @param {string} args.pathname
 * @param {boolean} [args.pageOpensChat]
 */
export function useNavCoach({ coach, hasRail, pathname, pageOpensChat = false }) {
  const [ownOpen, setOwnOpen] = useState(pageOpensChat);
  const [openedByPage, setOpenedByPage] = useState(pageOpensChat);
  const [wantFullscreen, setWantFullscreen] = useState(false);
  const openerRef = useRef(null);
  const registered = Boolean(coach?.chat);
  // Not measured yet counts as a rail: the column is CSS-hidden below `md`
  // anyway, and a measured phone closes it before paint.
  const defaultOpen =
    (registered ? Boolean(coach.defaultOpen) : pageOpensChat) && hasRail !== false;
  const onOpenChange = coach?.onOpenChange;

  useLayoutEffect(() => {
    setOwnOpen(defaultOpen);
    setOpenedByPage(defaultOpen);
    setWantFullscreen(false);
  }, [pathname, defaultOpen]);

  const open = Boolean(registered ? (coach.open ?? ownOpen) : pageOpensChat && ownOpen);
  const setOpen = useCallback(
    (next) => {
      if (next) openerRef.current = document.activeElement;
      onOpenChange?.(next);
      setOwnOpen(next);
      setOpenedByPage(false);
      if (!next) setWantFullscreen(false);
    },
    [onOpenChange],
  );
  const setFullscreen = useCallback((next) => {
    setOpenedByPage(false);
    setWantFullscreen(next);
  }, []);

  // The flag is still in the address only until the first time this runs.
  const openOnArrival = Boolean(coach?.openOnArrival);
  useEffect(() => {
    if (!openOnArrival || !hasOpenSabineFlag()) return;
    clearOpenSabineFlag();
    setOpen(true);
  }, [openOnArrival, setOpen]);

  useEffect(() => {
    if (open) return;
    const opener = openerRef.current;
    openerRef.current = null;
    // Only when focus went down with the chat: closing because search
    // opened leaves focus in the search field.
    const active = document.activeElement;
    const focusLost = !active || active === document.body;
    if (focusLost && opener?.isConnected) opener.focus();
  }, [open]);

  return {
    registered,
    open,
    instant: open && openedByPage,
    fullscreen: open && (wantFullscreen || hasRail === false),
    setOpen,
    setFullscreen,
  };
}
